import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

// Fee collection dashboard — laid out like the finance team's own workbook:
// summary + revenue + category table on the left, the pending-fee student list
// full height on the right. Everything is computed from the stored snapshot.
const money = v => '₹ ' + Number(v || 0).toLocaleString('en-IN');
// Fee heads read like 'Lunch Inst I (26-27)' — show just the meaningful part.
const shortHead = l => String(l || '').replace(/\s*Inst\s*[IVX]+\s*\(?[\d-]*\)?\s*$/i, '').trim() || l;

export default function FeeDashboard() {
  const [dates, setDates] = useState([]);
  const [date, setDate] = useState('');
  const [campus, setCampus] = useState('');
  const [campuses, setCampuses] = useState([]);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPending, setShowPending] = useState(false);   // pending list opens on click
  const [inst, setInst] = useState('all');                 // installment switch
  const [year, setYear] = useState('all');                 // academic-year switch
  const [boards, setBoards] = useState([]);
  const [board, setBoard] = useState('');                  // finance board filter
  const [years, setYears] = useState([]);                  // all years ever uploaded

  useEffect(() => {
    api.get('/master/campuses').then(r => setCampuses(r.data.filter(c => c.active))).catch(() => {});
    api.get('/fees/boards').then(r => setBoards(r.data)).catch(() => {});
  }, []);

  // Dates depend on the chosen year — selecting a year re-points the date list
  // (and drops a date that has no data for that year).
  useEffect(() => {
    api.get('/fees/dates', { params: year && year !== 'all' ? { year } : {} })
      .then(r => {
        setDates(r.data);
        setDate(cur => (r.data.some(x => x.as_of_date === cur) ? cur : (r.data[0] ? r.data[0].as_of_date : '')));
      }).catch(() => {});
  }, [year]);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (date) params.date = date;
    if (campus) params.campus_id = campus;
    if (board) params.board_id = board;
    if (inst && inst !== 'all') params.installment = inst;
    if (year && year !== 'all') params.year = year;
    api.get('/fees/dashboard', { params })
      .then(r => { setD(r.data); if (r.data && r.data.years && r.data.years.length) setYears(r.data.years); })
      .catch(() => setD(null))
      .finally(() => setLoading(false));
  }, [date, campus, board, inst, year]);
  useEffect(() => { load(); }, [load]);

  const Card = ({ label, value, color, onClick, hint }) => (
    <div className="card" onClick={onClick}
      style={{ padding: '14px 16px', textAlign: 'center', cursor: onClick ? 'pointer' : 'default',
        boxShadow: onClick ? '0 0 0 1px var(--accent)33' : undefined }}>
      <div style={{ fontSize: 12, color: 'var(--text2)' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: color || 'var(--text)' }}>{value}</div>
      {hint && <div style={{ fontSize: 10.5, color: 'var(--accent)' }}>{hint}</div>}
    </div>
  );

  return (
    <div>
      <div className="flex-gap" style={{ justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>
            💰 {d && !d.empty ? d.campus_name : 'Fee Collection'}
          </h2>
          <p style={{ fontSize: 13, color: 'var(--text2)' }}>
            {d && !d.empty ? `${d.installment_label} Fee Collection Summary · as of ${d.as_of_date}` : 'Upload a fee report to see the dashboard.'}
          </p>
        </div>
        <div className="flex-gap" style={{ gap: 8, flexWrap: 'wrap' }}>
          <select value={year} onChange={e => setYear(e.target.value)} style={{ padding: '6px 8px' }} title="Academic year">
            <option value="all">All years</option>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={campus} onChange={e => { setCampus(e.target.value); setBoard(''); }} style={{ padding: '6px 8px' }} title="Campus">
            <option value="">All campuses</option>
            {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={board} onChange={e => setBoard(e.target.value)} style={{ padding: '6px 8px' }} title="Board">
            <option value="">All boards</option>
            {boards.filter(b => !campus || String(b.campus_id) === String(campus))
                   .map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <select value={date} onChange={e => setDate(e.target.value)} style={{ padding: '6px 8px' }} title="Report date">
            {!dates.length && <option value="">No reports</option>}
            {dates.map(x => <option key={x.as_of_date} value={x.as_of_date}>{x.as_of_date} · {x.campuses} campus{x.campuses > 1 ? 'es' : ''}</option>)}
          </select>
        </div>
      </div>

      {/* Installment switch — every figure recomputes for the selection */}
      {d && !d.empty && d.installments && d.installments.length > 1 && (
        <div className="flex-gap" style={{ gap: 6, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--text2)' }}>Installment:</span>
          {['all', ...d.installments].map(x => (
            <button key={x} onClick={() => setInst(x)}
              className={`btn btn-sm ${inst === x ? 'btn-primary' : 'btn-secondary'}`}>
              {x === 'all' ? 'All' : x}
            </button>
          ))}
        </div>
      )}

      {loading ? <div className="card"><p style={{ color: 'var(--text2)' }}>Loading…</p></div>
       : !d || d.empty ? <div className="card"><div className="empty-state"><p>No fee report for this selection.</p></div></div> : (
        <div>
          {/* Summary, revenue, categories — full width now that the pending
              list opens on demand instead of taking a permanent column. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)', marginBottom: 8, letterSpacing: '.04em' }}>
                {inst === 'all' ? 'ALL INSTALLMENTS' : inst} — FEE COLLECTION SUMMARY
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
                <Card label="Total Students" value={d.students_total} />
                <Card label="Students Fully Paid" value={d.students_paid} color="var(--green)" />
                <Card label="Students with Pending" value={d.students_pending}
                  color={d.students_pending > 0 ? 'var(--red)' : 'var(--text)'}
                  onClick={d.students_pending > 0 ? () => setShowPending(true) : undefined}
                  hint={d.students_pending > 0 ? 'click to view list' : undefined} />
              </div>
            </div>

            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)', marginBottom: 8, letterSpacing: '.04em' }}>REVENUE SUMMARY</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
                <Card label="Total Revenue" value={money(d.total_amount)} />
                <Card label="Paid Revenue" value={money(d.paid_amount)} color="var(--green)" />
                <Card label="Pending Revenue" value={money(d.pending_amount)}
                  color={Number(d.pending_amount) > 0 ? 'var(--red)' : 'var(--text)'}
                  onClick={d.pending_students.length ? () => setShowPending(true) : undefined}
                  hint={d.pending_students.length ? 'click to view list' : undefined} />
              </div>
              <div style={{ marginTop: 10 }}>
                <div style={{ height: 8, background: 'var(--bg3)', borderRadius: 20, overflow: 'hidden' }}>
                  <div style={{ width: d.collected_pct + '%', height: '100%', background: 'var(--green)' }} />
                </div>
                <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>{d.collected_pct}% collected</div>
              </div>
            </div>

            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)', padding: '10px 12px', letterSpacing: '.04em' }}>CATEGORY — PAID / UNPAID (students)</div>
              <div className="table-wrap"><table>
                <thead><tr><th>Category</th><th>Paid</th><th>Unpaid</th><th>Collected</th></tr></thead>
                <tbody>
                  {d.categories.map(c => (
                    <tr key={c.label}>
                      <td>{shortHead(c.label)}</td>
                      <td style={{ color: 'var(--green)', fontWeight: 600 }}>{c.paid}</td>
                      <td style={{ color: Number(c.unpaid) > 0 ? 'var(--red)' : 'var(--text2)', fontWeight: 600 }}>{c.unpaid}</td>
                      <td style={{ fontSize: 12, color: 'var(--text2)' }}>{money(c.paid_amount)}</td>
                    </tr>
                  ))}
                  <tr style={{ background: 'var(--bg3)', cursor: d.pending_students.length ? 'pointer' : 'default' }}
                      onClick={() => d.pending_students.length && setShowPending(true)}>
                    <td style={{ fontWeight: 600 }}>Pending Fee {d.pending_students.length > 0 && <span style={{ fontSize: 10.5, color: 'var(--accent)', fontWeight: 400 }}>· view list</span>}</td>
                    <td style={{ color: 'var(--text2)' }}>—</td>
                    <td style={{ color: 'var(--red)', fontWeight: 700 }}>{d.students_pending}</td>
                    <td style={{ fontSize: 12, color: 'var(--red)' }}>{money(d.pending_amount)}</td>
                  </tr>
                </tbody>
              </table></div>
            </div>

            {d.by_campus && d.by_campus.length > 1 && (
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)', padding: '10px 12px', letterSpacing: '.04em' }}>BY BOARD</div>
                <div className="table-wrap"><table>
                  <thead><tr><th>Board</th><th>Campus</th><th>Students</th><th>Total</th><th>Paid</th><th>Pending</th><th>%</th></tr></thead>
                  <tbody>{d.by_campus.map(c => (
                    <tr key={c.board_id || c.campus_id}>
                      <td style={{ fontWeight: 500 }}>{c.board_name}</td>
                      <td style={{ fontSize: 12, color: 'var(--text2)' }}>{c.campus_name}</td><td>{c.students_total}</td>
                      <td>{money(c.total_amount)}</td>
                      <td style={{ color: 'var(--green)' }}>{money(c.paid_amount)}</td>
                      <td style={{ color: c.pending_amount > 0 ? 'var(--red)' : 'var(--text2)' }}>{money(c.pending_amount)}</td>
                      <td style={{ fontWeight: 600 }}>{c.collected_pct}%</td>
                    </tr>
                  ))}</tbody>
                </table></div>
              </div>
            )}
          </div>

        </div>
      )}

      {/* Pending fee student list — opens from the Pending tiles */}
      {showPending && d && !d.empty && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowPending(false)}>
          <div className="modal" style={{ maxWidth: 780, maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <span className="modal-title">Pending Fee — Student List</span>
              <button className="modal-close" onClick={() => setShowPending(false)}>×</button>
            </div>
            <div className="flex-gap" style={{ justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 12.5, color: 'var(--text2)' }}>
                {d.pending_students.length} student(s) with an outstanding balance · {d.campus_name} · as of {d.as_of_date}
              </span>
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--red)' }}>Total: {money(d.pending_amount)}</span>
            </div>
            {!d.pending_students.length ? <div className="empty-state"><p>Everyone has paid in full. 🎉</p></div> : (
              <div className="table-wrap"><table>
                <thead><tr><th>Student Name</th><th>ID No.</th><th>Grade &amp; Section</th>
                  {d.by_campus && d.by_campus.length > 1 && <th>Campus</th>}
                  <th style={{ textAlign: 'right' }}>Pending</th></tr></thead>
                <tbody>
                  {d.pending_students.map((s, i) => (
                    <tr key={i}>
                      <td style={{ fontSize: 12.5 }}>{s.name}</td>
                      <td className="mono" style={{ fontSize: 11.5 }}>{s.student_code}</td>
                      <td style={{ fontSize: 12, color: 'var(--text2)' }}>{s.grade_section}</td>
                      {d.by_campus && d.by_campus.length > 1 && <td style={{ fontSize: 12 }}>{s.campus_name}</td>}
                      <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--red)', whiteSpace: 'nowrap' }}>{money(s.pending_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
